import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Label, Select, Textarea } from "@/components/ui/form";
import { Card, CardBody } from "@/components/ui/card";
import { apiFetch } from "@/utils/api";

interface Customer {
  id: string;
  company_name: string;
  contacts: { id: string; name: string }[];
}

interface LineDraft {
  species: string;
  grade: string;
  dimensions: string;
  quantity: number;
  unit: string;
  description: string;
}

const emptyLine = (): LineDraft => ({
  species: "",
  grade: "",
  dimensions: "",
  quantity: 0,
  unit: "BF",
  description: "",
});

export function RfqNewPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [contactId, setContactId] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);

  useEffect(() => {
    apiFetch<{ customers: Customer[] }>("/customers")
      .then((d) => setCustomers(d.customers))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const data = await apiFetch<{ id: string }>("/rfqs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          customerContactId: contactId || undefined,
          requestedDeliveryDate: deliveryDate || undefined,
          notes,
          lines,
        }),
      });
      navigate(`/rfqs/${data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error && !customers.length) return <ErrorState message={error} />;

  return (
    <div>
      <PageHeader title="New RFQ" subtitle="Create a request for quote." />
      <Card className="max-w-3xl">
        <CardBody>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Customer *</Label>
            <Select value={customerId} onChange={(e) => { setCustomerId(e.target.value); setContactId(""); }} required>
              <option value="">Select customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Requested By (contact)</Label>
            <Select value={contactId} onChange={(e) => setContactId(e.target.value)}>
              <option value="">Select contact</option>
              {(selectedCustomer?.contacts ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Requested Delivery Date</Label>
            <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        <div className="space-y-2">
          <Label>Line Items</Label>
          {lines.map((line, i) => (
            <div key={i} className="grid gap-2 sm:grid-cols-6 border-b pb-3">
              <Input placeholder="Species" value={line.species} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], species: e.target.value }; setLines(next);
              }} />
              <Input placeholder="Grade" value={line.grade} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], grade: e.target.value }; setLines(next);
              }} />
              <Input placeholder="Dimensions" value={line.dimensions} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], dimensions: e.target.value }; setLines(next);
              }} />
              <Input type="number" placeholder="Qty" value={line.quantity || ""} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], quantity: Number(e.target.value) }; setLines(next);
              }} />
              <Select value={line.unit} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], unit: e.target.value }; setLines(next);
              }}>
                <option value="BF">BF</option>
                <option value="MBF">MBF</option>
                <option value="pcs">pcs</option>
                <option value="lf">lf</option>
              </Select>
              <Input placeholder="Notes" value={line.description} onChange={(e) => {
                const next = [...lines]; next[i] = { ...next[i], description: e.target.value }; setLines(next);
              }} />
            </div>
          ))}
          <Button type="button" variant="secondary" onClick={() => setLines([...lines, emptyLine()])}>
            Add Line
          </Button>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" disabled={submitting}>{submitting ? "Creating…" : "Create RFQ"}</Button>
      </form>
        </CardBody>
      </Card>
    </div>
  );
}
